import { api } from "../api";

const parseJSONSafe = (v, fallback = {}) => {
  try {
    return typeof v === "string" ? JSON.parse(v) : v || fallback;
  } catch {
    return fallback;
  }
};

const ts = (v) => {
  const d = new Date(String(v || "").replace(" ", "T"));
  return Number.isNaN(d.getTime()) ? 0 : d.getTime();
};

const toArray = (v) => (Array.isArray(v) ? v : v ? [v] : []);

const isOkPayload = (payload) => {
  if (!payload || typeof payload !== "object") return true;
  if (typeof payload.success === "boolean") return payload.success;
  if (typeof payload.ok === "boolean") return payload.ok;
  if (typeof payload.status === "string") {
    return !/(error|fail)/i.test(payload.status);
  }
  if (payload.error) return false;
  return true;
};

export function normalizeAnamnese(raw) {
  if (!raw) return null;
  const answers = parseJSONSafe(raw.anamnesis || raw.answers || {});
  const evaluation = parseJSONSafe(raw.evaluation);

  const persona = raw.persona || null;

  // Converte scores do v1 ({Key: val}) para o formato do PieChart ([{label, value}])
  let health_map = [];
  if (evaluation?.scores) {
    health_map = Object.entries(evaluation.scores).map(([label, value]) => ({
      label: label.toLowerCase(), // Normaliza para bater com o mapa do PieChartSaude
      value: value > 100 ? value / 10 : value, // Ajuste se for 0-1000 ou 0-100
    }));
  } else if (raw.map_score) {
    // Fallback para legacy map_score se vier do backend
    health_map = parseJSONSafe(raw.map_score, []);
  }

  const isDefault = Number(raw.is_default || raw.default || 0) === 1;

  return {
    id: raw.id ?? raw.anm_id,
    anm_id: raw.anm_id ?? raw.id,
    usr_id: raw.usr_id || raw.user_id,
    persona_id:
      raw.persona_id ??
      raw.person_id ??
      raw.profile_id ??
      persona?.per_id ??
      null,
    image: raw.image ?? raw.image_url ?? persona?.image_url ?? null,
    gender: raw.gender ?? persona?.gender ?? null,
    answers,
    evaluation,
    health_map,
    created_at: raw.created_at || raw.updated_at || null,
    origin: raw.origin || answers.origin || null,
    is_primary: Boolean(raw.is_primary ?? raw.primary ?? isDefault),
    is_default: isDefault ? 1 : 0,
    display_name:
      persona?.name ||
      raw.name ||
      raw.display_name ||
      (raw.id || raw.anm_id ? `Anamnese #${raw.id || raw.anm_id}` : "Anamnese"),
    persona,
  };
}

export function sortAnamneses(list) {
  const anamnesis = (list || [])
    .map(normalizeAnamnese)
    .filter(Boolean)
    .sort((a, b) => {
      const pri = Number(b.is_default) - Number(a.is_default);
      if (pri !== 0) return pri;
      const tb = ts(b.created_at);
      const ta = ts(a.created_at);
      return tb - ta;
    });
  return anamnesis;
}

export async function anamnesisSave() {
  try {
    const response = await api.post("anamnesisSave", {
      answers: localStorage.getItem("userAnswers"),
    });
    return response.data;
  } catch (error) {
    throw new Error("Erro ao salvar anamnese: " + error.message);
  }
}

export async function getUserAnamneses() {
  try {
    const { data } = await api.get("/users/anamneses");
    const list = data?.data || [];
    return sortAnamneses(list);
  } catch (err) {
    throw new Error("Erro ao buscar anamneses: " + err.message);
  }
}

export async function getUserPersonas() {
  try {
    const { data } = await api.get("/users/personas", {
      params: { _t: Date.now() },
    });
    return data?.data || [];
  } catch (err) {
    throw new Error("Erro ao buscar personas: " + err.message);
  }
}

export async function getPersonaById(personaId, params = {}) {
  if (!personaId) throw new Error("Parâmetro personaId obrigatório");
  try {
    const { data } = await api.get(`/users/personas/${personaId}/details`, {
      params,
    });
    const result = data?.data || {};
    if (data?.addresses && !result.addresses) {
      result.addresses = data.addresses;
    }
    if (data?.payment_methods && !result.payment_methods) {
      result.payment_methods = data.payment_methods;
    }
    if (data?.default_address && !result.default_address) {
      result.default_address = data.default_address;
    }
    return result;
  } catch (err) {
    throw new Error("Erro ao buscar persona: " + err.message);
  }
}

export async function getPersonaCartDetails(personaId) {
  if (!personaId) throw new Error("Parâmetro personaId obrigatório");
  return getPersonaById(personaId, {
    include: "addresses,payment_methods",
    addresses: 1,
    payment_methods: 1,
  });
}

export async function getAnamnesisDetails(anmId) {
  if (!anmId) throw new Error("Parâmetro anmId obrigatório");
  try {
    const { data } = await api.get(`/users/anamneses/${anmId}`);
    return normalizeAnamnese(data?.data || null);
  } catch (err) {
    throw new Error("Erro ao buscar detalhes da anamnese: " + err.message);
  }
}

export async function disableUserAnamnese(anmId) {
  if (!anmId) throw new Error("Parâmetro anmId obrigatório");
  try {
    const res = await api.delete(`/users/anamneses/${anmId}`);
    if (res?.status >= 200 && res?.status < 300) {
      return { success: true, data: res.data };
    }
    throw new Error(res?.data?.message || "Falha ao excluir anamnese");
  } catch (err) {
    throw new Error("Erro ao excluir anamnese: " + err.message);
  }
}

export async function setUserAnamnesePrimary(anmId) {
  if (!anmId) throw new Error("Parâmetro anmId obrigatório");
  try {
    const res = await api.post(`/users/anamneses/${anmId}/select`);
    if (res?.status >= 200 && res?.status < 300) {
      return { success: true, data: res.data };
    }
    throw new Error(res?.data?.message || "Falha ao definir principal");
  } catch (err) {
    throw new Error("Erro ao definir principal: " + err.message);
  }
}

export async function setUserPersonaPrimary(personaId) {
  if (!personaId) throw new Error("Parâmetro personaId obrigatório");
  try {
    // Chamada única e canônica: apenas corpo com per_id
    const res = await api.post(`/users/personas/${personaId}/set-default`);
    if (res?.status >= 200 && res?.status < 300 && isOkPayload(res?.data)) {
      return { success: true, data: res.data };
    }
    const msg =
      res?.data?.message ||
      res?.data?.error ||
      "Falha ao definir persona como principal";
    throw new Error(msg);
  } catch (err) {
    throw new Error("Erro ao definir persona principal: " + err.message);
  }
}

export async function deleteUserPersona(personaId) {
  if (!personaId) throw new Error("Parâmetro personaId obrigatório");
  try {
    const res = await api.delete(`/users/personas/${personaId}`, {
      data: { per_id: personaId },
    });
    if (res?.status >= 200 && res?.status < 300 && isOkPayload(res?.data)) {
      return { success: true, data: res.data };
    }
    const msg =
      res?.data?.message || res?.data?.error || "Falha ao excluir persona";
    throw new Error(msg);
  } catch (err) {
    throw new Error("Erro ao excluir persona: " + err.message);
  }
}

export async function updateUserAnamnese(anmId, payload) {
  if (!anmId) throw new Error("Parâmetro anmId obrigatório");
  try {
    const { data } = await api.patch(`/users/anamneses/${anmId}`, payload);
    return data?.data || null;
  } catch (err) {
    throw new Error(
      err?.response?.data?.message ||
        "Erro ao atualizar anamnese: " + err.message,
    );
  }
}
